import ServiceNavbar from '../shared/ServiceNavbar';
import ServiceHero from '../shared/ServiceHero';
import ServiceProcess from '../shared/ServiceProcess';
import ServiceFAQ from '../shared/ServiceFAQ';
import ServiceCTABlock from '../shared/ServiceCTABlock';
import Footer from '../../home/components/Footer';

const processSteps = [
  { icon: 'ri-palette-fill', step: 'Step 01', title: 'Design Consultation', description: 'We walk through your space, discuss your vision, and map out a lighting design that achieves it.' },
  { icon: 'ri-layout-grid-fill', step: 'Step 02', title: 'Layout Planning', description: 'Fixture placement, circuit layouts, and dimmer/control zones planned precisely before any work starts.' },
  { icon: 'ri-tools-fill', step: 'Step 03', title: 'Rough-In Wiring', description: 'All circuits, conduit, and boxes installed cleanly — invisible infrastructure that makes it work.' },
  { icon: 'ri-lightbulb-fill', step: 'Step 04', title: 'Fixture Installation', description: 'Fixtures, pendants, landscape lights, and smart controls installed with precision and care.' },
  { icon: 'ri-settings-3-fill', step: 'Step 05', title: 'Control & Tuning', description: 'Dimmers programmed, scenes set, and the final result dialed in until you love it.' },
];

const faqs = [
  { question: 'What types of specialty lighting do you install?', answer: 'We install recessed lighting (new and retrofit), under-cabinet lighting, cove and tray ceiling lighting, pendant and chandelier wiring, landscape and outdoor lighting systems, smart lighting controls, accent and artwork lighting, and whole-home dimmer system wiring.' },
  { question: 'Can you help me design the lighting layout, or do I need an interior designer?', answer: 'We have strong experience in residential lighting design and can help you plan an effective layout. For complex whole-home projects, we\'re happy to collaborate with your interior designer or architect. For most lighting upgrades, we can guide you through the design process ourselves.' },
  { question: 'What smart lighting systems do you work with?', answer: 'We install and wire for Lutron Caseta, RadioRA 2/3, Leviton Decora Smart, Kasa, and standard dimmer systems. We can also pre-wire for whole-home smart lighting integration with systems like Control4 or Crestron when coordinating with an integrator.' },
  { question: 'Do recessed lighting installs require patching the ceiling?', answer: 'Modern remodel-style recessed cans are designed to install through the existing ceiling without needing to open large areas. We minimize disruption and keep any necessary patching to an absolute minimum. We\'ll explain exactly what to expect before we start.' },
  { question: 'Can you add outdoor landscape lighting to an existing home?', answer: 'Yes. We install low-voltage landscape lighting systems, outdoor accent and path lighting, well lights, uplights for trees and architecture, and weatherproof outlet circuits for holiday and seasonal lighting needs.' },
];

const SpecialtyLightingPage = () => (
  <div className="min-h-screen bg-[#080C14]">
    <ServiceNavbar />

    <ServiceHero
      bgImage="https://public.readdy.ai/ai/img_res/8b50df11b38a306520363b963cb04ed2.jpg"
      label="Specialty Lighting Installation"
      headline="Illuminate Every Space,"
      headlineAccent="Beautifully"
      subtext="Precision-installed recessed lighting, landscape systems, smart controls, and architectural accent lighting. We make your home look exactly the way you imagined it — refined, warm, and perfectly lit."
      trustBadges={[
        { icon: 'ri-lightbulb-flash-fill', label: 'Design-Conscious' },
        { icon: 'ri-shield-star-fill', label: 'Licensed & Insured' },
        { icon: 'ri-remote-control-fill', label: 'Smart Controls' },
        { icon: 'ri-award-fill', label: '20+ Years Experience' },
      ]}
    />

    {/* Visual showcase overview */}
    <section className="bg-[#080C14] py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-4">The Service</span>
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
            Every Space Has a Lighting Story.<br />We Help You Tell It.
          </h2>
          <p className="text-[#5E7A96] text-[15px] max-w-xl mx-auto leading-relaxed">
            From recessed kitchen cans to full landscape systems, DOC Electric brings the same precision and care to specialty lighting that we bring to every electrical project.
          </p>
        </div>

        {/* Photo grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-16">
          {/* Large left */}
          <div className="lg:col-span-7 relative group overflow-hidden rounded-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=stunning%20kitchen%20recessed%20LED%20lighting%20installation%20modern%20luxury%20kitchen%20warm%20white%20light%20even%20illumination%20island%20pendant%20lights%20beautiful%20interior%20dark%20wood%20cabinets%20high%20end%20residential&width=900&height=560&seq=sl-showcase1&orientation=landscape"
              alt="Kitchen recessed lighting"
              className="w-full h-72 lg:h-[360px] object-cover object-top group-hover:scale-[1.02] transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080C14]/70 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5">
              <span className="bg-white/10 backdrop-blur-md text-white text-[12px] font-semibold px-3 py-1.5 rounded-full border border-white/15">Recessed Lighting</span>
            </div>
          </div>

          {/* Right column */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="relative group overflow-hidden rounded-2xl flex-1">
              <img
                src="https://readdy.ai/api/search-image?query=beautiful%20outdoor%20landscape%20lighting%20garden%20path%20low%20voltage%20lights%20trees%20uplighted%20warm%20evening%20luxury%20residential%20home%20landscaping%20professional%20exterior%20lighting%20nighttime&width=600&height=320&seq=sl-showcase2&orientation=landscape"
                alt="Landscape lighting"
                className="w-full h-40 object-cover object-top group-hover:scale-[1.02] transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080C14]/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="bg-white/10 backdrop-blur-md text-white text-[12px] font-semibold px-3 py-1.5 rounded-full border border-white/15">Landscape Systems</span>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl flex-1">
              <img
                src="https://readdy.ai/api/search-image?query=smart%20home%20lighting%20control%20panel%20dimmer%20switch%20modern%20living%20room%20elegant%20cove%20ceiling%20tray%20lighting%20accent%20sophisticated%20residential%20interior%20warm%20ambient%20lighting%20design&width=600&height=320&seq=sl-showcase3&orientation=landscape"
                alt="Smart lighting controls"
                className="w-full h-40 object-cover object-top group-hover:scale-[1.02] transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080C14]/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="bg-white/10 backdrop-blur-md text-white text-[12px] font-semibold px-3 py-1.5 rounded-full border border-white/15">Smart Controls</span>
              </div>
            </div>
          </div>
        </div>

        {/* Services list */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: 'ri-lightbulb-fill', title: 'Recessed Lighting', desc: 'New and retrofit LED recessed cans in kitchens, living areas, hallways, and bathrooms.' },
            { icon: 'ri-plant-fill', title: 'Landscape Lighting', desc: 'Path lights, uplights, well lights, and accent lighting for gardens, trees, and architecture.' },
            { icon: 'ri-remote-control-fill', title: 'Smart Dimmers & Controls', desc: 'Lutron, Leviton, and Kasa smart dimmer systems with app and voice control capability.' },
            { icon: 'ri-pantone-fill', title: 'Cove & Accent Lighting', desc: 'Tray ceiling, cove, and architectural accent lighting to create visual depth and drama.' },
            { icon: 'ri-gallery-fill', title: 'Artwork & Showcase Lighting', desc: 'Picture lights, adjustable track systems, and display lighting for galleries and collections.' },
            { icon: 'ri-building-2-fill', title: 'Outdoor & Security Lighting', desc: 'Floodlights, motion sensors, soffit lighting, and facade accent lighting for curb appeal.' },
          ].map((item) => (
            <div key={item.title} className="flex gap-4 p-5 bg-[#0D1320] rounded-xl border border-[#1A2538] hover:border-[#00D9FF]/20 transition-colors duration-300">
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#00D9FF]/10 flex-shrink-0">
                <i className={`${item.icon} text-[#00D9FF] text-base`} />
              </span>
              <div>
                <div className="text-white text-[13px] font-bold mb-1">{item.title}</div>
                <div className="text-[#3A566E] text-[12px] leading-snug">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Mid CTA with image */}
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=elegant%20luxury%20home%20exterior%20night%20time%20beautiful%20landscape%20lighting%20warm%20amber%20lights%20glowing%20windows%20curb%20appeal%20sophisticated%20residential%20architectural%20lighting%20dusk%20premium%20property&width=1920&height=500&seq=sl-midcta1&orientation=landscape"
          alt="Exterior lighting showcase"
          className="w-full h-full object-cover object-center"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#080C14]/95 via-[#080C14]/80 to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-lg">
          <h3 className="text-2xl lg:text-3xl font-black text-white mb-4">See Your Home in a New Light.</h3>
          <p className="text-[#7A9AB8] text-[15px] leading-relaxed mb-6">
            Great lighting transforms how a space feels. One consultation with DOC Electric and you&apos;ll start seeing the possibilities in every room.
          </p>
          <a href="#estimate" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#00D9FF] hover:bg-[#00c5e8] text-[#0A1628] font-bold text-[13px] uppercase tracking-wider rounded-lg transition-all duration-300 hover:shadow-[0_0_28px_rgba(0,217,255,0.4)] cursor-pointer whitespace-nowrap">
            Schedule a Lighting Consultation
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
          </a>
        </div>
      </div>
    </section>

    <ServiceProcess
      title="How Your Lighting Project Works"
      subtitle="From vision to finished installation — collaborative, clean, and precise."
      steps={processSteps}
      theme="dark"
    />

    {/* Benefits — moody cards */}
    <section className="bg-[#0D1320] py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-4">Why It Matters</span>
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">The Impact of Great Lighting</h2>
          <p className="text-[#5E7A96] text-[15px] max-w-xl mx-auto">Lighting is the single most impactful design element in any room. And when it&apos;s done well, you barely notice it — you just feel it.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: 'ri-emotion-fill', title: 'Sets the Mood', desc: 'The right lighting creates atmosphere — warm and intimate, bright and energizing, or dramatically focused.' },
            { icon: 'ri-leaf-fill', title: 'Energy Efficient', desc: 'LED upgrades and smart dimmers reduce energy use by 60–80% vs. older incandescent systems.' },
            { icon: 'ri-home-smile-fill', title: 'Increases Home Value', desc: 'Professionally installed lighting systems are a top home staging and resale advantage.' },
            { icon: 'ri-eye-fill', title: 'Reduces Eye Strain', desc: 'Properly balanced, glare-free lighting improves comfort and reduces fatigue — especially for workspaces.' },
          ].map((benefit) => (
            <div key={benefit.title} className="bg-[#080C14] rounded-2xl p-7 border border-[#1A2538] hover:border-[#00D9FF]/20 hover:-translate-y-1 transition-all duration-300">
              <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#00D9FF]/10 mb-5">
                <i className={`${benefit.icon} text-[#00D9FF] text-xl`} />
              </span>
              <h3 className="text-[16px] font-bold text-white mb-2">{benefit.title}</h3>
              <p className="text-[#3A566E] text-[13px] leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <ServiceFAQ faqs={faqs} theme="dark" />
    <ServiceCTABlock
      servicePreset="Specialty Lighting"
      headline="Ready to Transform Your Space?"
      subtext="Book a lighting consultation with DOC Electric. We'll walk your space, discuss your vision, and design a lighting plan that brings it to life."
    />
    <Footer />
  </div>
);

export default SpecialtyLightingPage;
